<template>
	<section>
		<h3 class="font-display text-4 mb-4">Plan Details</h3>
		<p class="mb-3 font-bold">Subscription to {{ viewModel.NewPlan.DisplayName }} ({{ formatCurrency(viewModel.NewPlan.MonthlyAmount, false) }}/Month)</p>
		<table class="w-full mb-4 border-t border-subtle">
			<tbody>
				<tr v-for="item in costModel.invoice.LineItems" :key="item.Id" class="border-b border-subtle">
					<th class="text-left py-3 font-normal">{{ item.Description }}</th>
					<td class="text-right py-3">{{ formatCurrency(item.Amount) }}</td>
				</tr>
				<tr>
					<th class="text-right">Subtotal:</th>
					<td class="text-right py-3">{{ formatCurrency(costModel.invoice.Subtotal) }}</td>
				</tr>
				<tr v-if="costModel.invoice.BalanceApplied < 0">
					<th class="text-right">Credits Applied:</th>
					<td class="text-right py-3">{{ formatCurrency(costModel.invoice.BalanceApplied) }}</td>
				</tr>
				<tr v-if="costModel.invoice.DiscountPercent > 0 && costModel.invoice.EligibleAmount !== 0 && costModel.invoice.BalanceApplied < 0">
					<th class="text-left py-3">Total Before Discount:</th>
					<td class="text-right py-3"> {{ formatCurrency(costModel.invoice.EligibleAmount) }}</td>
				</tr>
				<tr v-if="costModel.invoice.DiscountPercent > 0">
					<th class="text-right py-3 font-normal">{{ costModel.invoice.DiscountDescription }}:</th>
					<td class="text-right py-3">{{ formatCurrency(-costModel.invoice.DiscountAmount) }}</td>
				</tr>
				<tr v-if="costModel.invoice.BalanceApplied > 0 && costModel.invoice.DiscountAmount > 0">
					<th>Total:</th>
					<td class="text-right py-3"> {{ formatCurrency(costModel.invoice.DiscountedTotal) }}</td>
				</tr>
				<tr v-if="costModel.invoice.TaxAmount != 0 || costModel.invoice.TaxDisplayText">
					<th class="text-right">{{ costModel.invoice.TaxDisplayText || 'Sales Tax' }}:</th>
					<td class="text-right py-3">{{ formatCurrency(costModel.invoice.TaxAmount) }}</td>
				</tr>
				<tr v-if="costModel.invoice.TaxFootnote">
					<td colspan="2" class="text-right py-1 text-sm text-muted italic">{{ costModel.invoice.TaxFootnote }}</td>
				</tr>
				<tr v-if="costModel.invoice.BalanceApplied > 0">
					<th class="text-left py-3">Outstanding Balance:</th>
					<td class="text-right py-3">{{ formatCurrency(costModel.invoice.BalanceApplied) }}</td>
				</tr>
				<tr v-if="costModel.unpaidInvoiceAmount > 0">
					<th class="text-left py-3">Outstanding Balance:</th>
					<td class="text-right py-3">{{ formatCurrency(costModel.unpaidInvoiceAmount) }}</td>
				</tr>
				<tr v-if="amountDue >= 0">
					<th class="text-right text-3">{{ amountDueType }}</th>
					<td class="text-right py-3"><strong>{{ formatCurrency(amountDue) }}</strong></td>
				</tr>
			</tbody>
		</table>
		<CCallout v-if="showCallout" type="info" class="mb-4">
			<template v-if="viewModel.PrepaymentMonths > 0">
				After successful payment, your organization will have a credit balance of {{ formatCurrency(creditAmount, false) }}.
				This amount will automatically be applied to future monthly charges.
				Starting {{ billingPeriodStart }}, any monthly charges will automatically be paid from your credit balance.
			</template>

			<template v-else-if="viewModel.DiscountTrialPlan && viewModel.DiscountTrialPlan.Product === viewModel.NewPlan.Product">
				Your subscription will continue after your free trial has ended, and your credit card will be charged a pro-rated amount for any partial month of service.<br><br>
				You can change or cancel your subscription at any time.
			</template>

			<template v-else-if="viewModel.NewPlan.MonthlyAmount > 0">
				<template v-if="viewModel.CurrentBalance < 0">
					Any remaining credit balance will be applied to future charges, after which your credit card will automatically be billed {{ formatCurrency(viewModel.NewPlan.MonthlyAmount, false) }} monthly.
				</template>

				<template v-else>
					After successful payment, all features of your new plan will be available immediately. Your credit card will automatically be billed {{ formatCurrency(viewModel.NewPlan.MonthlyAmount, false) }} monthly starting {{ billingPeriodStart }}.
					<div v-if="costModel.invoice.AmountDue > 0 && costModel.invoice.AmountDue < .5"><br><br>Any amount that is less than $.50 will be included in the first month's invoice.</div>
					<div v-if="costModel.invoice.AmountDue < 0"><br><br>Any remaining credit will be applied to your next monthly invoice.</div>
				</template>

				<template v-if="viewModel.DiscountTrialPlan">
					Your subscription will automatically end after your free trial has ended.<br><br>
					You can change or cancel your trial at any time during the trial.
				</template>
			</template>

			<template v-else-if="viewModel.NewPlan.Product === 'Individual'">
				After downgrading to the Individual plan, any entries, storage, or users over the free plan's limits will be locked. You will receive a credit for the remaining time on your current plan for the month.<br><br>
				<div v-if="costModel.invoice.AmountDue < 0">Any remaining credit will be applied to your account and will be available for future purchases.</div>
			</template>

			<template v-if="viewModel.Delinquent && viewModel.NewPlan.Product !== 'Individual'">
				Your account is currently delinquent. Please note that any unpaid invoices will be automatically attempted. All unpaid invoices must be paid in full before a plan change will take effect.
			</template>
		</CCallout>
	</section>
</template>
<script lang="ts">
	import { defineComponent } from 'vue';
	import CCallout from 'src/components/content/Callout.vue';
	import type { LineItem } from '@cognitoforms/types/server-types/plans/model/line-item.ts';

	export default defineComponent({
		components: {
			CCallout
		},
		props: {
			viewModel: {
				type: Object, required: true
			},
			costModel: {
				type: Object, required: true
			}
		},
		computed: {
			amountDueType() {
				return this.costModel.invoice.AmountDue < 0 ? 'Credit Amount: ' : 'Amount Due: ';
			},
			amountDue() {
				return this.costModel.invoice.AmountDue + this.costModel.unpaidInvoiceAmount;
			},
			creditAmount() {
				return this.costModel.invoice.LineItems.reduce((sum: number, item: LineItem) => sum + (item.Id === null ? item.Amount : 0), 0) - this.costModel.invoice.StartingBalance;
			},
			billingPeriodStart() {
				// Viewmodel only contains end of month, add 1 day to get the start of month when billing occurs
				const startOfMonth = new Date(this.viewModel.EndOfMonth);
				startOfMonth.setDate(startOfMonth.getDate() + 1);
				return startOfMonth.toLocaleDateString('en-us', { month: '2-digit', day: '2-digit', year: '2-digit', timeZone: 'UTC' });
			},
			showCallout() {
				// The callout is empty for gratis plans
				return !this.viewModel.NewPlan.Code.includes('GRATIS');
			}
		},
		methods: {
			formatCurrency(amount: number, showCentsAlways: boolean = true) {
				const absAmount = Math.abs(amount);
				const hasDecimal = absAmount % 1 !== 0;
				const formatted = showCentsAlways || hasDecimal ? absAmount.toFixed(2) : absAmount.toString();
				const valString = '$' + this.formatNumber(formatted);
				return amount >= 0 ? valString : '(' + valString + ')';
			},
			formatNumber(num: string) {
				return num.replace(/\B(?=(?:\d{3})+(?!\d))/g, ',');
			}
		}
	});
</script>
